import { SEOHead } from '@/components/seo/SEOHead';
import { Badge } from '@/components/ui/badge';
import { SectionBadge, SectionDivider } from '@/components/marketing';
import { getAuthLink } from '@/lib/links';
import { Button } from '@/components/ui/button';
import {
  Calculator,
  Users,
  Map,
  Receipt,
  FolderKanban,
  Target,
  Zap,
  Check,
  ArrowRight,
} from 'lucide-react';
export const ChangelogPage = () => {
  const featureCategories = [
    {
      category: 'Lead Management & Attribution',
      icon: <Target className="size-5 text-cyan-400" />,
      color: 'bg-slate-800/80 border border-slate-700 text-cyan-400',
      features: [
        {
          title: 'Complete Lead Attribution System',
          date: 'January 2026',
          description:
            'Track every lead from first click to closed deal with complete source attribution',
          businessValue:
            'Know exactly which marketing channels drive revenue. Redirect budget from ineffective channels to high-performing ones, potentially increasing ROI by 200-300%.',
          impact: 'High',
        },
        {
          title: 'URL Parameter Campaign Tracking',
          date: 'January 2026',
          description:
            'Automatically tag every lead with campaign source, medium, and content parameters',
          businessValue:
            'Measure true ROI of every marketing campaign. Focus budget on campaigns that drive both leads and revenue.',
          impact: 'High',
        },
        {
          title: 'Call Tracking with Dynamic Number Pools',
          date: 'January 2026',
          description:
            'Assign unique phone numbers to different marketing campaigns for complete call attribution',
          businessValue:
            'Identify high-performing campaigns that drive phone calls (typically 3-5x higher conversion rates than web forms).',
          impact: 'High',
        },
      ],
    },
    {
      category: 'Territory & Canvassing',
      icon: <Map className="size-5 text-emerald-400" />,
      color: 'bg-slate-800/80 border border-slate-700 text-emerald-400',
      features: [
        {
          title: 'Territory Wizard with Polygon Validation',
          date: 'January 2026',
          description:
            'Step-by-step territory creation with real-time polygon validation and feedback',
          businessValue:
            'Create sales territories in minutes instead of hours. Eliminate territorial conflicts and ensure complete market coverage.',
          impact: 'High',
        },
        {
          title: 'Sales Rep Dashboard Filtering',
          date: 'January 2026',
          description: 'All KPIs, stats, and map overlays scoped to assigned sales rep',
          businessValue:
            'Sales reps see only their territories and leads. Improve accountability and performance tracking.',
          impact: 'Medium',
        },
        {
          title: 'Storm Targeting & Automated Canvassing',
          date: 'December 2025',
          description: 'Identify storm-affected areas and create targeted canvassing territories',
          businessValue:
            'Be first to market after storms hit. Storm work typically represents 30-50% of annual revenue.',
          impact: 'High',
        },
      ],
    },
    {
      category: 'Estimation & Pricing',
      icon: <Calculator className="size-5 text-blue-400" />,
      color: 'bg-slate-800/80 border border-slate-700 text-blue-400',
      features: [
        {
          title: 'AI Satellite Integration',
          date: 'December 2025',
          description:
            'Satellite-based roof measurement tool with high-res imagery for 90% of US addresses',
          businessValue:
            'Get instant roof measurements without site visits. Save 2-3 hours per estimate and reduce travel costs.',
          impact: 'High',
        },
        {
          title: 'Multi-Tier Pricing System',
          date: 'December 2025',
          description:
            'Economy, Standard, and Premium pricing tiers with automatic complexity calculations',
          businessValue: 'Present multiple options to customers, increasing close rates by 25-40%.',
          impact: 'High',
        },
      ],
    },
    {
      category: 'Project Management',
      icon: <FolderKanban className="size-5 text-slate-200" />,
      color: 'bg-slate-800/80 border border-slate-700 text-slate-200',
      features: [
        {
          title: '6-Stage Project Pipeline',
          date: 'December 2025',
          description:
            'Complete workflow from lead to estimate to proposal to contract to production to completion',
          businessValue:
            'Never lose track of a project. Reduce project delays by 30% and improve customer communication.',
          impact: 'High',
        },
        {
          title: 'Real-Time Project Tracking',
          date: 'December 2025',
          description: 'Track every job with timelines, team assignments, and financial tracking',
          businessValue:
            'Know exactly where every project stands. Reduce missed deadlines and improve cash flow.',
          impact: 'Medium',
        },
      ],
    },
    {
      category: 'Financial Management',
      icon: <Receipt className="size-5 text-emerald-400" />,
      color: 'bg-slate-800/80 border border-slate-700 text-emerald-400',
      features: [
        {
          title: 'Professional Invoice Generation',
          date: 'December 2025',
          description:
            'Generate professional invoices with line items, tax calculations, and payment tracking',
          businessValue:
            'Get paid faster with professional invoices. Reduce payment delays by 20-30%.',
          impact: 'High',
        },
        {
          title: 'Financial Analytics Dashboard',
          date: 'December 2025',
          description:
            'Real-time dashboards showing revenue, costs, profit margins, and pipeline value',
          businessValue:
            'Make data-driven financial decisions. Identify profitable vs unprofitable projects in real-time.',
          impact: 'High',
        },
      ],
    },
    {
      category: 'Team Collaboration',
      icon: <Users className="size-5 text-indigo-400" />,
      color: 'bg-slate-800/80 border border-slate-700 text-indigo-400',
      features: [
        {
          title: 'Team Member Invitation Workflow',
          date: 'December 2025',
          description: '7-step invitation to operational workflow with real-time permission sync',
          businessValue:
            'Onboard new team members in minutes. Reduce setup time from days to hours.',
          impact: 'Medium',
        },
        {
          title: 'Role-Based Access Control',
          date: 'December 2025',
          description:
            'Customizable roles and permissions for sales reps, office staff, and crew members',
          businessValue:
            'Ensure team members see only what they need. Improve security and reduce training time.',
          impact: 'Medium',
        },
      ],
    },
    {
      category: 'Platform & Infrastructure',
      icon: <Zap className="size-5 text-cyan-400" />,
      color: 'bg-slate-800/80 border border-slate-700 text-cyan-400',
      features: [
        {
          title: 'Marketing Website Overhaul',
          date: 'January 2026',
          description: 'Complete redesign with SEO optimization and faster load times',
          businessValue:
            'Better online presence drives more qualified leads. Improved SEO increases organic traffic.',
          impact: 'Medium',
        },
        {
          title: 'Multi-Tenant Architecture',
          date: 'December 2025',
          description: 'Complete data isolation and security for multi-client operations',
          businessValue:
            'Scale from solo operator to enterprise team. Maintain security and performance at any size.',
          impact: 'High',
        },
      ],
    },
  ];

  return (
    <>
      <SEOHead
        title="Product Updates"
        description="Rooflyzer changelog: new features, updates, improvements. See what's new in our roofing software every month."
        canonicalUrl="/changelog"
        schema={{
          '@context': 'https://schema.org',
          '@type': 'BreadcrumbList',
          itemListElement: [
            { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://rooflyzer.pro' },
            {
              '@type': 'ListItem',
              position: 2,
              name: 'Changelog',
              item: 'https://rooflyzer.pro/changelog',
            },
          ],
        }}
      />

      <section className="relative pt-28 pb-16 px-4 sm:px-6 text-center overflow-hidden">
        <div className="relative z-10 max-w-4xl mx-auto">
          <SectionBadge icon={Zap} text="Product Changelog" color="cyan" className="mb-5" />
          <h1 className="hero-h1 font-bold text-white mb-5">
            Building the Complete <span className="text-white">Trade Operating System</span>
          </h1>
          <p className="hero-lead text-white/60 max-w-3xl mx-auto">
            Every feature we add is designed to solve real problems roofing contractors face every
            day. We ship updates weekly &mdash; here&apos;s what we&apos;ve built.
          </p>
        </div>
      </section>

      <SectionDivider className="max-w-5xl mx-auto px-4 mb-0" />

      <section className="py-20 px-4 sm:px-6">
        <div className="max-w-6xl mx-auto space-y-12">
          {featureCategories.map((category, categoryIndex) => (
            <div
              key={categoryIndex}
              className="glass-panel-interactive rounded-3xl p-8 reveal-on-scroll border border-slate-800 bg-slate-900/40"
            >
              <div className="flex items-center gap-4 mb-8">
                <div
                  className={`w-12 h-12 rounded-2xl ${category.color} flex items-center justify-center`}
                >
                  {category.icon}
                </div>
                <div>
                  <h2 className="section-h3 font-bold text-white">{category.category}</h2>
                  <p className="text-white/40 text-sm">
                    {category.features.length} feature{category.features.length !== 1 ? 's' : ''}
                  </p>
                </div>
              </div>

              <div className="space-y-5">
                {category.features.map((feature, featureIndex) => (
                  <div
                    key={featureIndex}
                    className="glass-panel rounded-2xl p-5 border border-slate-800 hover:border-slate-700 transition-colors"
                  >
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex-1">
                        <div className="flex flex-wrap items-center gap-2 mb-1">
                          <h3 className="section-h3 font-bold text-white">{feature.title}</h3>
                          <Badge
                            variant="outline"
                            className={
                              feature.impact === 'High'
                                ? 'border-emerald-500/30 text-emerald-400 bg-emerald-500/10 text-xs font-mono'
                                : 'border-slate-700 text-slate-400 bg-slate-800/50 text-xs font-mono'
                            }
                          >
                            {feature.impact} Impact
                          </Badge>
                        </div>
                        <p className="text-white/35 text-xs font-mono mb-2">{feature.date}</p>
                        <p className="text-white/65 text-sm leading-relaxed">
                          {feature.description}
                        </p>
                      </div>
                    </div>
                    <div className="rounded-xl p-4 bg-slate-900/60 border border-slate-800">
                      <div className="flex items-start gap-3">
                        <Check className="size-4 text-emerald-400 flex-shrink-0 mt-0.5" />
                        <div>
                          <p className="text-xs font-semibold text-emerald-400 mb-0.5">
                            Business Impact:
                          </p>
                          <p className="text-white/60 text-xs leading-relaxed">
                            {feature.businessValue}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </section>

      <SectionDivider className="max-w-5xl mx-auto px-4 mt-4 mb-0" />

      <section className="relative py-20 px-4 text-center overflow-hidden">
        <div className="relative z-10 max-w-2xl mx-auto">
          <div className="glass-panel-elevated rounded-3xl p-8 tech-grid-bg border border-slate-800">
            <h2 className="section-h2 font-bold text-white mb-3">More Features Coming Soon</h2>
            <p className="body-lg text-white/55 mb-6">
              We ship updates weekly. Join early access to be first to know about new features.
            </p>
            <Button
              size="lg"
              className="bg-white text-slate-950 hover:bg-slate-100 font-semibold shadow-sm"
              onClick={() => window.location.assign(getAuthLink('/register'))}
            >
              Start Free Today
              <ArrowRight className="ml-2 size-4" />
            </Button>
          </div>
        </div>
      </section>
    </>
  );
};

export default ChangelogPage;
